import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LayoutDashboard, Coins, Sparkles, UserRound, LogOut, Users, ShieldAlert, Compass, LifeBuoy, CreditCard } from "lucide-react";
import { Logo } from "@/components/layout/Logo";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/lib/auth";
import { useGuide } from "@/lib/guide/context";
import { initials } from "@/lib/format";
import { useT } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const publicLinks = [
  { to: "/marketplace", key: "marketplace" },
  { to: "/coaches", key: "coaches" },
];

function linkClass({ isActive }) {
  return `text-sm font-medium transition-colors hover:text-foreground ${
    isActive ? "text-foreground" : "text-muted-foreground"
  }`;
}

export function Navbar() {
  const { isAuthed, user, logout } = useAuth();
  const { isCoach, showBar } = useGuide();
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onSignOut = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border/70 glass">
      <div className="container-page flex h-16 items-center justify-between">
        <div className="flex items-center gap-8">
          <Logo />
          <nav className="hidden items-center gap-6 md:flex">
            {publicLinks.map((l) => (
              <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.key}`}>
                {t(`nav.${l.key}`)}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <LanguageSwitcher />
          {isAuthed && (
            <>
              <button onClick={() => navigate("/app/account/support")} data-testid="nav-support-btn" title={t("nav.support")} className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                <LifeBuoy className="h-4 w-4" />
              </button>
              <button onClick={() => navigate("/app/billing")} data-testid="nav-billing-btn" title={t("nav.billing")} className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
                <CreditCard className="h-4 w-4" />
              </button>
            </>
          )}
          {isAuthed ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-full border border-border bg-card px-2 py-1 pr-3 transition-colors hover:bg-accent" data-testid="user-menu-trigger">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary text-xs text-primary-foreground">
                      {initials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="max-w-[120px] truncate text-sm font-medium">{user?.name || t("nav.account_fallback")}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/app")} data-testid="menu-dashboard">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> {t("nav.dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate(user?.role === "coach" ? "/app/coach/circles" : "/app/circles")} data-testid="menu-circles">
                  <Users className="mr-2 h-4 w-4" /> {t("nav.circles")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/app/credits")} data-testid="menu-credits">
                  <Coins className="mr-2 h-4 w-4" /> {t("nav.credits")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/app/membership")} data-testid="menu-membership">
                  <Sparkles className="mr-2 h-4 w-4" /> {t("nav.membership")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/app/account")} data-testid="menu-account">
                  <UserRound className="mr-2 h-4 w-4" /> {t("nav.account")}
                </DropdownMenuItem>
                {isCoach && showBar && (
                  <DropdownMenuItem onClick={() => showBar()} data-testid="menu-guide">
                    <Compass className="mr-2 h-4 w-4" /> {t("guide.bar.reopen")}
                  </DropdownMenuItem>
                )}
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/app/admin")} data-testid="menu-admin">
                    <ShieldAlert className="mr-2 h-4 w-4" /> Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onSignOut} data-testid="menu-signout">
                  <LogOut className="mr-2 h-4 w-4" /> {t("nav.signout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" asChild data-testid="nav-login-btn">
                <Link to="/login">{t("nav.login")}</Link>
              </Button>
              <Button className="rounded-full px-5" asChild data-testid="nav-signup-btn">
                <Link to="/signup">{t("nav.signup")}</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-6 flex flex-col gap-4">
                {publicLinks.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="text-base font-medium text-foreground"
                  >
                    {t(`nav.${l.key}`)}
                  </NavLink>
                ))}
                <div className="my-2 h-px bg-border" />
                {isAuthed ? (
                  <>
                    <Link to="/app" onClick={() => setOpen(false)} className="text-base font-medium">{t("nav.dashboard")}</Link>
                    <Link to={user?.role === "coach" ? "/app/coach/circles" : "/app/circles"} onClick={() => setOpen(false)} className="text-base font-medium">{t("nav.circles")}</Link>
                    <Link to="/app/credits" onClick={() => setOpen(false)} className="text-base font-medium">{t("nav.credits")}</Link>
                    <Link to="/app/membership" onClick={() => setOpen(false)} className="text-base font-medium">{t("nav.membership")}</Link>
                    <Link to="/app/billing" onClick={() => setOpen(false)} className="text-base font-medium">{t("nav.billing")}</Link>
                    <Link to="/app/account/support" onClick={() => setOpen(false)} className="text-base font-medium">{t("nav.support")}</Link>
                    <Link to="/app/account" onClick={() => setOpen(false)} className="text-base font-medium">{t("nav.account")}</Link>
                    <Button variant="outline" className="mt-2 rounded-full" onClick={() => { setOpen(false); onSignOut(); }}>
                      {t("nav.signout")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" className="rounded-full" asChild onClick={() => setOpen(false)}>
                      <Link to="/login">{t("nav.login")}</Link>
                    </Button>
                    <Button className="rounded-full" asChild onClick={() => setOpen(false)}>
                      <Link to="/signup">{t("nav.signup")}</Link>
                    </Button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
